import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, {
	type CustomTriggerProps,
	DropdownItem,
	DropdownItemGroup,
	type OnOpenChangeArgs,
} from '@atlaskit/dropdown-menu';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';

import type { NavItemActionsProps } from '../../types';

import messages from './messages';
import { NavItemReorderingMenu } from './nav-item-reordering';
import { NavItemVisibilityToggle } from './nav-item-visibility';

export function NavItemActionsMenu({
	isReorderable = true,
	isVisible = true,
	moveUp,
	moveDown,
	moveToTop,
	moveToBottom,
	toggleVisibility,
	canMoveUp = true,
	canMoveDown = true,
	canToggleVisibility = true,
	onOpenChange,
}: NavItemActionsProps) {
	const { formatMessage } = useIntl();
	// We need to control the state so that nested menus can close this one when they close
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const handleOpenChange = useCallback(
		(args: OnOpenChangeArgs) => {
			setIsOpen(args.isOpen);
			onOpenChange?.(args);
		},
		[onOpenChange],
	);

	if (!isReorderable && !canToggleVisibility) {
		return null;
	}

	return (
		<DropdownMenu
			trigger={({ triggerRef, ...props }: CustomTriggerProps) => (
				<IconButton
					{...props}
					ref={triggerRef}
					icon={ShowMoreHorizontalIcon}
					label={formatMessage(messages.actionsMenuTriggerLabel)}
					onClick={(event) => handleOpenChange({ isOpen: !isOpen, event })}
				/>
			)}
			shouldRenderToParent
			spacing="compact"
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
		>
			<DropdownItemGroup>
				{isReorderable && (
					<NavItemReorderingMenu
						TriggerComponent={DropdownItem}
						canMoveUp={canMoveUp}
						canMoveDown={canMoveDown}
						moveUp={moveUp}
						moveDown={moveDown}
						moveToTop={moveToTop}
						moveToBottom={moveToBottom}
						onOpenChange={handleOpenChange}
					/>
				)}
				{canToggleVisibility && (
					<NavItemVisibilityToggle
						TriggerComponent={DropdownItem}
						isVisible={isVisible}
						toggleVisibility={toggleVisibility}
					/>
				)}
			</DropdownItemGroup>
		</DropdownMenu>
	);
}
